/**
 * Commission entry composable — Feb 2026 Cleanup Ship.
 *
 * Percent-first UX: the percentage input is the primary control. The dollar
 * amount is auto-derived as `funding_amount × pct / 100`.
 *
 * Prerequisite handling: when `funding_amount` is missing on the deal, the
 * percent input is disabled and an inline panel surfaces the prerequisite +
 * the dollar fallback (so operators are never stuck with a dead UI).
 *
 * Props:
 *   funding_amount: number|null
 *   value:    { pct: string|number, amount: string|number }
 *   onChange: ({ pct, amount }) => void
 *   testidPrefix: e.g. "commission-paid" or "broker-commission"
 *   pctLabel / amountLabel: optional overrides
 *   disabled: boolean
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { AlertCircle } from 'lucide-react';

function _fmtAmount(n) {
  if (!Number.isFinite(n)) return '';
  // Two decimals max, trimmed.
  return (Math.round(n * 100) / 100).toString();
}

function _fmtPct(amount, funding) {
  if (!Number.isFinite(amount) || !Number.isFinite(funding) || funding <= 0) return '';
  const p = (amount / funding) * 100;
  return (Math.round(p * 10000) / 10000).toString();
}

export default function CommissionEntryFields({
  funding_amount,
  value,
  onChange,
  testidPrefix = 'commission',
  pctLabel = 'Percentage of funding',
  amountLabel = 'Amount ($)',
  disabled = false,
}) {
  const funding = Number.isFinite(parseFloat(funding_amount)) ? parseFloat(funding_amount) : null;
  const fundingMissing = !funding || funding <= 0;

  const pctValue = value?.pct ?? '';
  const amountValue = value?.amount ?? '';

  const handlePctChange = (raw) => {
    const pct = raw === '' ? '' : raw;
    let amount = '';
    const pNum = parseFloat(raw);
    if (raw !== '' && Number.isFinite(pNum) && funding) {
      amount = _fmtAmount(funding * pNum / 100);
    }
    onChange({ pct, amount });
  };

  const handleAmountChange = (raw) => {
    const amount = raw === '' ? '' : raw;
    let pct = '';
    const aNum = parseFloat(raw);
    if (raw !== '' && Number.isFinite(aNum) && funding) {
      pct = _fmtPct(aNum, funding);
    }
    onChange({ pct, amount });
  };

  return (
    <div className="space-y-3">
      {fundingMissing && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
          data-testid={`${testidPrefix}-funding-prerequisite-panel`}
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          <div>
            <div className="font-medium">Funding amount needed</div>
            <p className="mt-1">
              Set the deal's funding amount first to enter commission as a percentage.
              You can still enter the dollar amount directly below.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`${testidPrefix}-pct-input`}>{pctLabel}</Label>
        <div className="relative">
          <Input
            id={`${testidPrefix}-pct-input`}
            data-testid={`${testidPrefix}-pct-input`}
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={pctValue}
            onChange={(e) => handlePctChange(e.target.value)}
            disabled={disabled || fundingMissing}
            className="pr-7"
          />
          <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-slate-500">%</span>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${testidPrefix}-amount-input`}>{amountLabel}</Label>
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-xs text-slate-500">$</span>
          <Input
            id={`${testidPrefix}-amount-input`}
            data-testid={`${testidPrefix}-amount-input`}
            type="number"
            step="0.01"
            placeholder="0.00"
            value={amountValue}
            onChange={(e) => handleAmountChange(e.target.value)}
            disabled={disabled}
            className="pl-6"
          />
        </div>
        {funding && Number.isFinite(parseFloat(amountValue)) && amountValue !== '' && (
          <p className="text-xs text-muted-foreground" data-testid={`${testidPrefix}-derived-readout`}>
            = {((parseFloat(amountValue) / funding) * 100).toFixed(2)}% of ${funding.toLocaleString()} funding
          </p>
        )}
      </div>
    </div>
  );
}
